import { getDeviceId } from '../helpers/utils/getDeviceId';
import { Vehicle } from '../interface/booking-service.interface';
import api from './api';
import apiWithRefreshToken from './apiWithRefreshToken';

const getClynder = ({ brand, model }: { brand: string; model: string }) => {
  return api.post('customer/v1/tradein/cylinder', null, { brand, model });
};

const getMileages = async () => {
  const response = await api.get('saphybris-proxy/v1/saphybris/get-olx-mileage');
  return response?.data;
};

const getCarType = ({
  brand,
  carCylinder,
  model,
}: {
  brand: string;
  carCylinder: string;
  model: string;
}) => {
  return api.post('customer/v1/tradein/type', null, { brand, carCylinder, model });
};

const getTransmission = ({
  brand,
  carCylinder,
  model,
  carType,
}: {
  brand: string;
  carCylinder: string;
  model: string;
  carType: string;
}) => {
  return api.post('customer/v1/tradein/transmission', null, { brand, model, carCylinder, carType });
};

const getProdYear = ({
  brand,
  carCylinder,
  model,
  carType,
  carTransmission,
}: {
  carTransmission: string;
  brand: string;
  carCylinder: string;
  model: string;
  carType: string;
}) => {
  return api.post('customer/v1/tradein/prod-year', null, {
    brand,
    model,
    carCylinder,
    carType,
    carTransmission,
  });
};

const getFuel = ({
  brand,
  carCylinder,
  model,
  carType,
  carTransmission,
  year,
}: {
  year: number;
  carTransmission: string;
  brand: string;
  carCylinder: string;
  model: string;
  carType: string;
}) => {
  return api.post('customer/v1/tradein/fuel', null, {
    brand,
    model,
    carCylinder,
    carType,
    carTransmission,
    year,
  });
};

const getDrive = ({
  brand,
  carCylinder,
  model,
  carType,
  carTransmission,
  year,
  carFuel,
}: {
  carFuel: string;
  year: number;
  carTransmission: string;
  brand: string;
  carCylinder: string;
  model: string;
  carType: string;
}) => {
  return api.post('customer/v1/tradein/drive', null, {
    brand,
    model,
    carCylinder,
    carType,
    carTransmission,
    year,
    carFuel,
  });
};

const getCars = () => {
  return api.get('customer/v1/tradein');
};

const getVehicleMaster = (params: { brand?: string; modelName?: string }) => {
  const paramsString = new URLSearchParams([...Object.entries(params)]).toString();
  return api.get(`/saphybris-proxy/v1/saphybris/vehicles/vehicle-master?${paramsString}`);
};

const postVehicle = (payload: {
  carBrand: string;
  carModel: string;
  carType: string;
  carModelCode: string;
  productionYear: string;
  licencePlate: string;
  transmission: string;
  isNewVehicle: string;
  isTradeIn: string;
}): Promise<{
  data: {
    acknowledge: number;
    vehicles?: Vehicle[];
    tradeInVehicle: Vehicle;
  };
}> => {
  return api.post('api/post-vehicle-trade-in', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const postSubmitTradeIn = async (payload: {
  phoneNumber: string;
  articleType: string;
  otpValue: string | null;
  otpType: string | null;
  leadType: string;
  data: {
    title: string;
    tradeInType: string;
    name: string;
    phone: string;
    email: string;
    branchCode: string;
    city: string;
    provinsi: string;
    purchasePeriod: string;
    prospectVariant: string;
    usedCarPoliceRegNo: string;
    usedCarCity: string;
    mileage: string;
    mileageValue: string;
    source: string;
    addressLine1: string;
    addressLine2: string;
    isClosePopup: boolean;
    sourceSystem: string;
    entryPage: string;
    inspectionDate: string;
    inspectionTime: string;
  };
}): Promise<any> => {
  const deviceId = await getDeviceId();
  return api.post(
    'api/submit-trade-in',
    null,
    { ...payload, data: { ...payload.data, deviceId } },
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
};

const getTradeInFilterTypes = (accessToken: string) => {
  return api.get(`/saphybris-proxy/v1/saphybris/trade-in/filter/tradein-types`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const getTradeInList = (
  {
    page = 0,
    size = 10,
    productType = [],
    orderStatus = [],
  }: { page?: number; size?: number; productType?: string[]; orderStatus?: string[] },
  accessToken: string
) => {
  return api.get(`/saphybris-proxy/v1/saphybris/orders/my-order-list`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: {
      orderCategory: 'trade_in',
      page,
      size,
      orderStatus: orderStatus.join(','),
      productType: productType.join(','),
    },
  });
};

const getTradeInListAll = (
  {
    productType = [],
    orderStatus = [],
  }: { page?: number; size?: number; productType?: string[]; orderStatus?: string[] },
  accessToken: string
) => {
  return api.get(`/saphybris-proxy/v1/saphybris/orders/my-order-list`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: {
      orderCategory: 'trade_in',
      orderStatus: orderStatus.join(','),
      productType: productType.join(','),
    },
  });
};

const getTradeInListClient = ({
  page = 0,
  size = 10,
  productType = [],
  orderStatus = [],
}: {
  page?: number;
  size?: number;
  productType?: string[];
  orderStatus?: string[];
}) => {
  const params: {
    orderCategory: string;
    orderStatus?: string;
    productType?: string;
    page: number;
    size: number;
  } = {
    orderCategory: 'trade_in',
    page,
    size,
  };
  if (orderStatus.length > 0) {
    params.orderStatus = orderStatus.join(',');
  }
  if (productType.length > 0) {
    params.productType = productType.join(',');
  }
  return apiWithRefreshToken.get(`/api/trade-in/get-list`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params,
  });
};

const getTradeInListAllClient = ({
  productType = [],
  orderStatus = [],
}: {
  page?: number;
  size?: number;
  productType?: string[];
  orderStatus?: string[];
}) => {
  const params: {
    orderCategory: string;
    orderStatus?: string;
    productType?: string;
  } = {
    orderCategory: 'trade_in',
  };
  if (orderStatus.length > 0) {
    params.orderStatus = orderStatus.join(',');
  }
  if (productType.length > 0) {
    params.productType = productType.join(',');
  }
  return apiWithRefreshToken.get(`/api/trade-in/get-list`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params,
  });
};

export const getTradeInDetail = (accessToken: string, orderNo: string) => {
  return api.get(`/saphybris-proxy/v1/saphybris/trade-in/${orderNo}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const postReviewTradeIn = (payload: {
  description: string;
  rating: number;
  recommendation: number | number[];
  orderNumber: string;
  orderType: string;
  id: string;
}) => {
  return apiWithRefreshToken.post('/api/trade-in/submit-feedback', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const getTradeInPageContent = () => {
  return api.get(`parameter/v1/trade-in/content`);
};

const getTrackerNewCarTradeIn = (orderNo: string) => {
  return apiWithRefreshToken.get(`/api/trade-in/tracker-new-car?orderNo=${orderNo}`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const getListVehicleTradeIn = (accessToken) => {
  return apiWithRefreshToken.get('/saphybris-proxy/v1/saphybris/vehicles/list?stpOnly=true', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export default {
  getClynder,
  getCarType,
  getTransmission,
  getProdYear,
  getFuel,
  getDrive,
  getCars,
  getVehicleMaster,
  postVehicle,
  postSubmitTradeIn,
  getMileages,
  getTradeInList,
  getTradeInListClient,
  getTradeInFilterTypes,
  getTradeInDetail,
  postReviewTradeIn,
  getTradeInPageContent,
  getTradeInListAllClient,
  getTradeInListAll,
  getTrackerNewCarTradeIn,
  getListVehicleTradeIn,
};
